import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, CheckCircle, ArrowRight, AlertTriangle, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const benefits = [
  "The 10 mistakes that get disputes auto-rejected",
  "Why 'dispute everything' doesn't work (and what does)",
  "How to spot errors you didn't know you had",
  "The timing trick that doubles your success rate",
  "Free sample dispute letter included",
];

const LeadMagnet = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const triggerDownload = () => {
    const link = document.createElement('a');
    link.href = '/guides/credit-mistakes-guide.pdf';
    link.download = '10-Credit-Mistakes-Guide.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    
    // Validate name length
    if (trimmedName.length > 100) {
      toast({
        title: "Name too long",
        description: "Name must be 100 characters or less.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate email length and format
    if (trimmedEmail.length > 255) {
      toast({
        title: "Email too long",
        description: "Email must be 255 characters or less.",
        variant: "destructive",
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('submit-form', {
        body: {
          formType: 'email_subscription',
          data: {
            email: trimmedEmail,
            name: trimmedName,
            source: 'lead_magnet',
            tags: ['free_guide', 'credit_mistakes'],
          }
        }
      });
      
      if (error) throw error;
      if (data?.error) {
        if (data.code === 'RATE_LIMIT_EXCEEDED') {
          toast({
            title: "Too many requests",
            description: "Please try again later.",
            variant: "destructive",
          });
          return;
        }
        throw new Error(data.error);
      }
      
      if (data?.alreadySubscribed) {
        toast({
          title: "You're already subscribed! 📧",
          description: "Here's your guide again!",
        });
        triggerDownload();
        setName("");
        setEmail("");
        return;
      }
      
      toast({
        title: "Success! Your download is starting 🎉",
        description: "Check your downloads folder.",
      });
      triggerDownload();
      setName("");
      setEmail("");
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="free-guide" className="py-12 lg:py-16 bg-gradient-hero relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-10 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-3xl shadow-custom-lg p-8 lg:p-12 border border-border/50">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-destructive/10 rounded-full px-4 py-2 mb-6">
                  <AlertTriangle className="w-4 h-4 text-destructive-dark" />
                  <span className="text-sm font-semibold text-destructive-dark">Don't make these mistakes</span>
                </div>

                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Free Guide:{" "}
                  <span className="text-gradient">10 Credit Mistakes to Avoid</span>
                </h2>

                <p className="text-muted-foreground mb-6">
                  Most DIY credit disputes fail because of these avoidable errors. Learn what forum users wish they knew before sending their first dispute.
                </p>

                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-foreground text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Form */}
              <div className="bg-muted/30 rounded-2xl p-6 lg:p-8 border border-border/50">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-gold flex items-center justify-center shadow-gold">
                    <Gift className="w-8 h-8 text-accent-foreground" />
                  </div>
                </div>

                <h3 className="text-xl font-bold text-foreground text-center mb-2">
                  Get Your Free Guide
                </h3>
                <p className="text-muted-foreground text-center mb-6 text-sm">
                  Join 10,000+ who fixed their credit the right way
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your first name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                  <Button 
                    type="submit" 
                    variant="hero" 
                    size="lg" 
                    className="w-full group"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      "Processing..."
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Free Guide
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
